import React, { useState, useMemo } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import { FormattedMessage } from 'react-intl';
import FiltersSidebar from './FiltersSidebar';
import TrainingTable from './TrainingTable';
import { TrainingTableSortColumn, ViewMode, ViewContext } from './types';
import { SortDirection } from '../../../Types/SortDirection';
import { filterTrainings, sortTrainings } from './utils';
import { ALL_FILTER_VALUE, TrainingViewMode } from '../types/constants';
import UsersSidebar from './UsersSidebar';
import { Profile } from '../../../Types/Profile';
import { ExtendedUser } from '../../../Types';
import Card from '@amzn/meridian/card/card';
import CardHeader from '@amzn/meridian/card/card-header';
import useInspireOptions from '../../../hooks/useInspireOptions';
import AllAssociatesBanner from './AllAssociatesBanner';
import SelectedAssociateBanner from './SelectedAssociateBanner';
import useAssignedTraining from '../hooks/useAssignedTraining';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';

interface TrainingListProps {
  profile: Profile;
}

const TrainingList: React.FC<TrainingListProps> = ({ profile }) => {
  // Individual state variables following the established pattern
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.ALL_ASSOCIATES);
  const [selectedDirectReport, setSelectedDirectReport] = useState<
    ExtendedUser | undefined
  >(undefined);
  const [selectedSharedProfile, setSelectedSharedProfile] = useState<
    ExtendedUser | undefined
  >(undefined);

  // Filters state variables
  const [ccxMetricFilter, setCcxMetricFilter] = useState(ALL_FILTER_VALUE);
  const [trainingStatusFilter, setTrainingStatusFilter] =
    useState(ALL_FILTER_VALUE);

  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<TrainingTableSortColumn>(
    TrainingTableSortColumn.DUE_DATE
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.DESCENDING
  );
  const [showAssociatesSidebar, setShowAssociatesSidebar] =
    useState<boolean>(true);
  const [showFiltersSidebar, setShowFiltersSidebar] = useState<boolean>(false);
  const { cscEntityPrefix } = useInspireOptions();

  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);

  // sharedProfileCoachees from the parent is the aggregaged data consisted of direct reports and shared coachees
  const sharedCoachees = profile.sharedProfileCoachees.filter(
    coachee =>
      !profile.directReports.some(report => report.userID === coachee.userID)
  );
  const sharedCoacheesIDs = sharedCoachees.map(coachee => coachee.userID);

  const { loading, error, trainings } = useAssignedTraining(
    profile,
    cscEntityPrefix,
    TrainingViewMode.TM
  );

  // Filter and sort trainings based on current state
  const filteredAndSortedTrainings = useMemo(() => {
    let filteredTrainings = trainings;
    // Filter by view mode and selected direct report
    if (viewMode === ViewMode.INDIVIDUAL_ASSOCIATE && selectedDirectReport) {
      filteredTrainings = filteredTrainings.filter(
        training => training.associateId === selectedDirectReport.userID
      );
    }
    // Filter by view mode and selected shared profile
    if (viewMode === ViewMode.INDIVIDUAL_ASSOCIATE && selectedSharedProfile) {
      filteredTrainings = filteredTrainings.filter(
        training => training.associateId === selectedSharedProfile.userID
      );
    }
    // For ViewMode.ALL_ASSOCIATES, show all trainings (but filter out the trainings from shared coachees)
    if (viewMode === ViewMode.ALL_ASSOCIATES) {
      filteredTrainings = filteredTrainings.filter(
        training => !sharedCoacheesIDs.includes(training.associateId)
      );
    }

    // Apply filters and search
    filteredTrainings = filterTrainings(
      filteredTrainings,
      ccxMetricFilter,
      trainingStatusFilter,
      searchTerm
    );

    // Sort trainings
    filteredTrainings = sortTrainings(
      filteredTrainings,
      sortColumn,
      sortDirection
    );

    return filteredTrainings;
  }, [
    trainings,
    viewMode,
    selectedDirectReport,
    selectedSharedProfile,
    ccxMetricFilter,
    trainingStatusFilter,
    searchTerm,
    sortColumn,
    sortDirection,
  ]);

  const handleDirectReportSelection = (
    directReport: ExtendedUser | undefined
  ) => {
    if (directReport) {
      // Individual associate selected
      setViewMode(ViewMode.INDIVIDUAL_ASSOCIATE);
      setSelectedDirectReport(directReport);
      setSelectedSharedProfile(undefined);
    } else {
      // "My Team" selected - show all associates
      setViewMode(ViewMode.ALL_ASSOCIATES);
      setSelectedDirectReport(undefined);
      setSelectedSharedProfile(undefined);
    }
  };

  const handleSharedProfileSelection = (
    sharedProfile: ExtendedUser | undefined
  ) => {
    if (sharedProfile) {
      // Individual associate selected
      setViewMode(ViewMode.INDIVIDUAL_ASSOCIATE);
      setSelectedSharedProfile(sharedProfile);
      setSelectedDirectReport(undefined);
    } else {
      // "My Team" selected - show all associates
      setViewMode(ViewMode.ALL_ASSOCIATES);
      setSelectedSharedProfile(undefined);
      setSelectedDirectReport(undefined);
    }
  };

  const handleResetFilters = () => {
    setCcxMetricFilter(ALL_FILTER_VALUE);
    setTrainingStatusFilter(ALL_FILTER_VALUE);
  };

  const handleSearchChange = (searchTermValue: string | undefined) => {
    // If empty string, set to undefined to show all results
    setSearchTerm(searchTermValue ?? undefined);
  };

  const handleSort = (
    column: TrainingTableSortColumn,
    direction: SortDirection
  ) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const handleToggleSidebars = () => {
    // Toggle between showing associates sidebar and filters sidebar
    const newShowAssociatesSidebar = !showAssociatesSidebar;
    setShowAssociatesSidebar(newShowAssociatesSidebar);
    setShowFiltersSidebar(!newShowAssociatesSidebar);
  };

  const handleCloseSidebars = () => {
    // When closing filters, return to showing associates sidebar
    setShowAssociatesSidebar(true);
    setShowFiltersSidebar(false);
  };

  if (loading) {
    return (
      <Row
        type='outline'
        width='100%'
        alignmentHorizontal='center'
        height='400px'
      >
        <Loader />
      </Row>
    );
  }

  if (error) {
    return (
      <Box height='400px' width='fill' spacingInset='300'>
        <Alert type='error'>
          <FormattedMessage
            id='cs_perfportal_training_experience_error_message'
            defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
          />
        </Alert>
      </Box>
    );
  }

  return (
    <Card spacingInset='500 300 500 500'>
      <Column>
        <Row spacingInset='none none 500 none'>
          <CardHeader>
            <Heading level={2} type='h500'>
              <FormattedMessage
                id='cs_perfportal_training_experience_associate_training_statuses'
                defaultMessage='Associate training statuses'
              />
            </Heading>
          </CardHeader>
        </Row>
        <Row width='100%' alignmentVertical='top'>
          {/* Left Sidebar Column - Associates (320px fixed width, only when visible) */}
          {showAssociatesSidebar && (
            <Column
              width='320px'
              spacingInset='none 200 none none'
              alignmentVertical='top'
            >
              <UsersSidebar
                directReports={profile.directReports}
                selectedDirectReport={selectedDirectReport}
                onSelectDirectReport={handleDirectReportSelection}
                sharedProfiles={sharedCoachees}
                selectedSharedProfile={selectedSharedProfile}
                onSelectSharedProfile={handleSharedProfileSelection}
                loading={loading}
              />
            </Column>
          )}

          {/* Main Content Area - Takes remaining space */}
          <Column width='100%' alignmentVertical='top'>
            <Box type='outline' width='100%' spacingInset='200'>
              <Column alignmentVertical='top'>
                {/* Header Section - Conditional Banner */}
                {viewMode === ViewMode.INDIVIDUAL_ASSOCIATE &&
                selectedDirectReport ? (
                  <SelectedAssociateBanner
                    selectedDirectReport={selectedDirectReport}
                  />
                ) : viewMode === ViewMode.INDIVIDUAL_ASSOCIATE &&
                  selectedSharedProfile ? (
                  <SelectedAssociateBanner
                    selectedDirectReport={selectedSharedProfile}
                  />
                ) : (
                  <AllAssociatesBanner
                    searchValue={searchValue}
                    onSearchValueChange={setSearchValue}
                    onSearchSubmit={() => handleSearchChange(searchValue)}
                    onToggleSidebars={handleToggleSidebars}
                  />
                )}

                {/* Table Section */}
                <Box spacingInset='300'>
                  <TrainingTable
                    trainings={filteredAndSortedTrainings ?? []}
                    loading={loading}
                    error={error}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    viewContext={ViewContext.TM}
                    onSort={handleSort}
                    selectedCSA={selectedDirectReport || selectedSharedProfile}
                    viewMode={viewMode}
                  />
                </Box>
              </Column>
            </Box>
          </Column>

          {/* Right Sidebar Column - Filters (320px fixed width, only when visible) */}
          {showFiltersSidebar && (
            <Column width='320px' alignmentVertical='top'>
              <FiltersSidebar
                ccxMetricFilter={ccxMetricFilter}
                trainingStatusFilter={trainingStatusFilter}
                setCcxMetricFilter={setCcxMetricFilter}
                setTrainingStatusFilter={setTrainingStatusFilter}
                handleResetFilters={handleResetFilters}
                onClose={handleCloseSidebars}
              />
            </Column>
          )}
        </Row>
      </Column>
    </Card>
  );
};

export default TrainingList;
